import { useCallback, useState } from 'react';
import { TEAM_POLL_OPTIONS_MAX, TeamPostCreate } from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

// The London Life team chat, pinned at the top of every player's Chats: announcements and votes. Posting
// needs content.publish and a recent two-step check; a post can be taken down, never edited or deleted.
export function TeamChatPage() {
  const { can } = useAdmin();
  const posts = useRequest(useCallback(() => api.admin.teamPosts(), []));
  const [writing, setWriting] = useState<'announcement' | 'poll' | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [hours, setHours] = useState('24');
  const [done, setDone] = useState<string | null>(null);
  const start = (kind: 'announcement' | 'poll') => {
    setTitle('');
    setBody('');
    setOptions(['', '']);
    setHours('24');
    setDone(null);
    setWriting(kind);
  };
  const draft =
    writing === 'poll'
      ? { kind: 'poll' as const, title, body, options: options.filter((option) => option.trim()), hours: Number(hours) }
      : { kind: 'announcement' as const, title, body };
  const checked = TeamPostCreate.safeParse(draft);
  const post = async () => {
    if (!checked.success) throw new Error(checked.error.issues[0]?.message ?? 'Check the post');
    await api.admin.postToTeam(checked.data);
    setDone('Posted. Players see it in the team chat now.');
    await posts.reload();
  };
  const takeDown = async (id: string) => {
    try {
      await api.admin.takeDownTeamPost(id);
      setDone('Taken down.');
      await posts.reload();
    } catch (problem) {
      posts.fail(problem);
    }
  };
  return (
    <section>
      <PageHeader
        title="Team chat"
        description="Announcements and votes from the London Life team, pinned at the top of every player's Chats."
      />
      {can('content.publish') && (
        <div className="admin-toolbar">
          <Button type="button" onClick={() => start('announcement')}>
            New announcement
          </Button>
          <Button type="button" variant="outline" onClick={() => start('poll')}>
            New vote
          </Button>
        </div>
      )}
      {posts.error && <ErrorNotice error={posts.error} onRetry={() => void posts.reload()} />}
      {done && (
        <p role="status" className="admin-muted">
          {done}
        </p>
      )}
      {posts.data && !posts.data.posts.length && (
        <div className="admin-empty">
          <strong className="admin-empty-title">Nothing posted yet.</strong>
        </div>
      )}
      <ul className="admin-reports">
        {posts.data?.posts.map((item) => (
          <li key={item.id} className="admin-panel" data-testid="team-post">
            <div className="admin-player-head">
              <h2>
                {item.kind === 'poll' ? '🗳️ ' : '📣 '}
                {item.title}
              </h2>
              {item.removedAt ? <Badge variant="secondary">Taken down</Badge> : <Badge>Live</Badge>}
            </div>
            {item.body && <p className="admin-muted">{item.body}</p>}
            {item.kind === 'poll' && (
              <dl className="admin-facts">
                {item.options.map((option) => (
                  <div key={option.id} style={{ display: 'contents' }}>
                    <dt>{option.label}</dt>
                    <dd>{option.votes ?? 0} votes</dd>
                  </div>
                ))}
                <dt>Closes</dt>
                <dd>{item.closesAt ? when(item.closesAt) : '—'}</dd>
              </dl>
            )}
            <p className="admin-muted">Posted {when(item.createdAt)}</p>
            {!item.removedAt && can('content.publish') && (
              <div className="admin-page-actions wrap">
                <Button type="button" variant="outline" onClick={() => void takeDown(item.id)}>
                  Take down
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <FormDialog
        open={!!writing}
        title={writing === 'poll' ? 'New vote' : 'New announcement'}
        description="Every player sees it at the top of their Chats. It cannot be edited once posted."
        submitLabel="Post"
        canSubmit={checked.success}
        onSubmit={post}
        onClose={() => setWriting(null)}
      >
        <div className="admin-field">
          <Label htmlFor="team-title">{writing === 'poll' ? 'Question' : 'Title'}</Label>
          <Input id="team-title" maxLength={80} value={title} onChange={(event) => setTitle(event.target.value)} />
        </div>
        <div className="admin-field">
          <Label htmlFor="team-body">{writing === 'poll' ? 'More about it (optional)' : 'Message'}</Label>
          <Textarea id="team-body" maxLength={600} value={body} onChange={(event) => setBody(event.target.value)} />
        </div>
        {writing === 'poll' && (
          <>
            {options.map((option, index) => (
              <div className="admin-field" key={index}>
                <Label htmlFor={`team-option-${index}`}>Choice {index + 1}</Label>
                <Input
                  id={`team-option-${index}`}
                  maxLength={60}
                  value={option}
                  onChange={(event) =>
                    setOptions(options.map((item, at) => (at === index ? event.target.value : item)))
                  }
                />
              </div>
            ))}
            {options.length < TEAM_POLL_OPTIONS_MAX && (
              <Button type="button" variant="outline" onClick={() => setOptions([...options, ''])}>
                Add a choice
              </Button>
            )}
            <div className="admin-field">
              <Label htmlFor="team-hours">Open for (hours)</Label>
              <Input
                id="team-hours"
                type="number"
                min={1}
                max={336}
                value={hours}
                onChange={(event) => setHours(event.target.value)}
              />
            </div>
          </>
        )}
      </FormDialog>
    </section>
  );
}
